import get from 'lodash/get';
import baseState from './baseState';
import tuner from '../tuner';
import nowPlayingPoller from '../nowPlayingPoller';
import listenTimeLogger from '../loggers/listenTime';
import streamEvents from '../player/constants/streamEvents';
import playerName from '../player/constants/playerName';

let lastReconnectionAttemptTime = 0;
let currentTune;
let bufferStartTime = 0;

const SUBSCRIPTION_REQUIRED_REGEX =
  /service\/Audio\/subscriptionrequired.enUS.mp3/;

function shouldReconnectOnError() {
  // This specifically fixes the case where a stream rapidly loops on connecting -> playing -> error
  // The issue has only been seen on auto-playback for Safari 11.0 when autoplay is disabled
  return Date.now() - lastReconnectionAttemptTime > 1000;
}

function sendBufferReport() {
  listenTimeLogger.logBuffer(currentTune, bufferStartTime);
  tuner.streamPlayer.remove(streamEvents.play, sendBufferReport);
  tuner.streamPlayer.remove(streamEvents.positionChanged, sendBufferReport);
  bufferStartTime = 0;
}

function transitionToWaitingState() {
  tuner.changeState(tuner.states.waiting, { currentTune, wasPlaying: true });
}

function onError(errorData) {
  listenTimeLogger.logFailure(currentTune);

  if (shouldReconnectOnError()) {
    const currentStream = tuner.streamPlayer.getCurrentStream();
    const isBoostStation = get(currentStream, 'isBoostStation', false);
    lastReconnectionAttemptTime = Date.now();
    tuner.trigger(tuner.events.error, errorData);
    tuner.changeState(tuner.states.connecting, {
      forceShoutcast: !isBoostStation,
      playBoostExperience: isBoostStation,
    });
  } else {
    tuner.changeState(tuner.states.failed, errorData);
  }
}

function onSeeked() {
  // Web Media Player plays automatically on seek, so no need to call play().
  if (tuner.streamPlayer.player.name !== playerName.WebMediaPlayer) {
    tuner.streamPlayer.play();
  }
}

// meta will be deprecated eventually when we replace the music-metadata dependency and patch the
// abstraction leak of propagating this metadata to the app layer. The data handled by this event
// should be folded into the new `metadata` event below.
function onMeta(data) {
  tuner.trigger(tuner.events.meta, data);
}

function onMetadata(metadata) {
  nowPlayingPoller.setMetadataOverrides(metadata);
}

function onMediaEnded(stream = {}) {
  tuner.trigger(tuner.events.mediaEnded, stream);

  if (!stream.isBoostTransition) {
    listenTimeLogger.logEnd(currentTune);
  }

  const isSubscriptionRequired = SUBSCRIPTION_REQUIRED_REGEX.test(stream.url);

  if (stream.nextGuideId && !isSubscriptionRequired) {
    tuner.trigger(tuner.events.nextTune, stream);

    tuner
      .load(
        {
          guideId: stream.nextGuideId,
          params: get(tuner, 'tune.params', {}),
          ignoreStreamPosition: true,
          formats: tuner.tune.formats,
          type: tuner.tune.type,
          itemToken: stream.itemToken,
        },
        tuner.events.nextTune,
      )
      .then(() => {
        tuner.play();
      })
      .catch((error) => {
        error.errorData = { reason: 'play next failed' };
        tuner.changeState(tuner.states.failed, error);
      });
  } else if (!stream.isBoostTransition) {
    tuner.changeState(tuner.states.stopped);
  }
}

function onPositionChanged(times) {
  if (times.isLiveSeekStream) {
    tuner.tune.liveSeekStreamPosition = times.elapsedPercent;
  }

  tuner.trigger(tuner.events.positionChanged, times);
}

const onNowPlayingChanged = tuner.trigger.bind(
  null,
  tuner.events.nowPlayingChanged,
);

function bindPlaybackHandlers(context) {
  if (context.bufferStartTime) {
    bufferStartTime = context.bufferStartTime;
    tuner.streamPlayer.once(tuner.streamPlayer.events.play, sendBufferReport);
    tuner.streamPlayer.once(
      tuner.streamPlayer.events.positionChanged,
      sendBufferReport,
    );
  }

  tuner.streamPlayer.on(
    tuner.streamPlayer.events.positionChanged,
    onPositionChanged,
  );
  tuner.streamPlayer.on(tuner.streamPlayer.events.mediaEnded, onMediaEnded);
  tuner.streamPlayer.on(
    tuner.streamPlayer.events.waiting,
    transitionToWaitingState,
  );
  tuner.streamPlayer.on(tuner.streamPlayer.events.error, onError);
  tuner.streamPlayer.on(tuner.streamPlayer.events.seeked, onSeeked);
  tuner.streamPlayer.on(tuner.streamPlayer.events.meta, onMeta);
  tuner.streamPlayer.on(tuner.streamPlayer.events.metadata, onMetadata);

  nowPlayingPoller.start(
    tuner.boostState.parentGuideId || currentTune.guideId,
    onNowPlayingChanged,
  );
}

const state = {
  name: 'playing',
  enter: function (previousState, context = {}) {
    currentTune = {
      ...tuner.tune,
      listenId: tuner.listenId,
    };

    tuner.trigger(tuner.events.playing, tuner.streamPlayer.stream);
    bindPlaybackHandlers(context);

    if (
      !context.continueCurrentLogger &&
      !tuner.streamPlayer.stream.isBoostTransition
    ) {
      listenTimeLogger.startPeriodicLogger(currentTune);
    }
  },
  exit: function () {
    bufferStartTime = 0;

    tuner.streamPlayer.remove(tuner.streamPlayer.events.play, sendBufferReport);
    tuner.streamPlayer.remove(
      tuner.streamPlayer.events.positionChanged,
      sendBufferReport,
    );
    tuner.streamPlayer.remove(
      tuner.streamPlayer.events.positionChanged,
      onPositionChanged,
    );
    tuner.streamPlayer.remove(
      tuner.streamPlayer.events.mediaEnded,
      onMediaEnded,
    );
    tuner.streamPlayer.remove(
      tuner.streamPlayer.events.waiting,
      transitionToWaitingState,
    );
    tuner.streamPlayer.remove(tuner.streamPlayer.events.error, onError);
    tuner.streamPlayer.remove(tuner.streamPlayer.events.seeked, onSeeked);
    tuner.streamPlayer.remove(tuner.streamPlayer.events.meta, onMeta);
    tuner.streamPlayer.remove(tuner.streamPlayer.events.metadata, onMetadata);

    nowPlayingPoller.stop();
  },
  pause: function () {
    tuner.streamPlayer.pause();
    listenTimeLogger.logPause(currentTune);
    tuner.changeState(tuner.states.paused);
  },
  stop: function () {
    tuner.streamPlayer.stop();
    listenTimeLogger.logStop(currentTune);
    tuner.changeState(tuner.states.stopped);
  },
  seek: function (position) {
    tuner.streamPlayer.seek(position);
    listenTimeLogger.logShift(position, currentTune);
  },
  load: function (promise, triggerEvent) {
    if (triggerEvent !== tuner.events.nextTune) {
      tuner.trigger(tuner.events.stopped);
    }

    tuner.changeState(tuner.states.loading, promise);
  },
};

export default {
  ...baseState,
  ...state,
};
